import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { ReactComponent as USAOutline } from './files/map_outline_v2.svg';
import tourData from '../data/tourStops.json';
import stopPhotos from './stopPhotos';

// Toggle to show/hide the react-simple-maps geography layer (for alignment)
const SHOW_GEO_LAYER = false;

// US states TopoJSON - continental US only
const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Hover photo card dimensions (SVG units)
const CARD_WIDTH = 280;
const CARD_PAD = 10;
const IMAGE_WIDTH = CARD_WIDTH - CARD_PAD * 2;
const IMAGE_HEIGHT = 175;
const LABEL_HEIGHT = 30;
const CARD_HEIGHT = CARD_PAD + IMAGE_HEIGHT + LABEL_HEIGHT;

function USAMap({ skipIntro = false, vanImage = null }) {
  const [hoveredPin, setHoveredPin] = useState(null);
  // Clicking a pin locks its photo open until another click or another pin is hovered
  const [lockedPin, setLockedPin] = useState(null);
  // Clicking the photo card opens the full-size image
  const [lightboxStop, setLightboxStop] = useState(null);
  const activePin = hoveredPin || lockedPin;
  const tourStops = tourData.tourStops;

  return (
    <div className="usa-map-container">
      <div className="usa-map-wrapper">
        {vanImage}
        {/* Your custom SVG outline - adjust position to align with the map */}
        <USAOutline
          className="usa-map-svg-overlay"
          style={{
            position: 'absolute',
            zIndex: 1,
            pointerEvents: 'none',
            width: '100%',
            height: 'auto',
            top: '62px',
            left: '6px',
            scale: '1.08',
            opacity: 1,
          }}
        />
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{
            scale: 1200
          }}
          style={{
            width: '100%',
            height: 'auto',
            position: 'relative',
            zIndex: 2,
            overflow: 'visible'
          }}
        >
          {/* USA Geography - only shown when SHOW_GEO_LAYER is true (for alignment) */}
          {SHOW_GEO_LAYER && (
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="transparent"
                    stroke="#e11d38"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none' },
                      pressed: { outline: 'none' }
                    }}
                  />
                ))
              }
            </Geographies>
          )}

          {/* Tour Stop Pins - render active pin last so photo card appears on top */}
          {[...tourStops].sort((a, b) => {
            if (a.id === activePin) return 1;
            if (b.id === activePin) return -1;
            return 0;
          }).map((stop, index) => {
            const isLaunch = stop.type === 'launch';
            // Position photo card on left for pins on the right side of map (longitude > -88)
            const tooltipOnLeft = stop.coordinates[0] > -88;
            const cardX = tooltipOnLeft ? -(CARD_WIDTH + 18) : 18;
            // Launch cards get an extra line pointing to the Flickr album
            const cardHeight = CARD_HEIGHT + (isLaunch ? 22 : 0);
            const cardY = -cardHeight / 2;
            const photo = stopPhotos[stop.id];

            return (
              <Marker key={stop.id} coordinates={stop.coordinates}>
                <g
                  onMouseEnter={() => {
                    setHoveredPin(stop.id);
                    // Hovering a different pin releases the locked one
                    if (lockedPin && lockedPin !== stop.id) {
                      setLockedPin(null);
                    }
                  }}
                  onMouseLeave={() => setHoveredPin(null)}
                  onClick={() => {
                    // Launch stars open their Flickr album; stops lock the photo open
                    if (stop.flickrUrl) {
                      window.open(stop.flickrUrl, '_blank', 'noopener');
                    } else {
                      setLockedPin(lockedPin === stop.id ? null : stop.id);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <motion.g
                    initial={{ scale: skipIntro ? 1 : 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: skipIntro ? 0 : 0.3 + index * 0.05,
                      duration: 0.4,
                      ease: 'backOut'
                    }}
                  >
                    {isLaunch ? (
                      // Star for launch parties
                      <path
                        d="M0,-8 L2,-2.5 L8,-2.5 L3.5,1.5 L5.5,8 L0,4 L-5.5,8 L-3.5,1.5 L-8,-2.5 L-2,-2.5 Z"
                        transform="scale(1.85)"
                        fill="#DC143C"
                        stroke="white"
                        strokeWidth={1.5}
                        style={{
                          filter: 'drop-shadow(0 1px 2px rgba(220, 20, 60, 0.5))'
                        }}
                      />
                    ) : (
                      // Circle for regular stops
                      <circle
                        r={5}
                        fill="#DC143C"
                        stroke="white"
                        strokeWidth={2}
                        style={{
                          filter: 'drop-shadow(0 1px 2px rgba(220, 20, 60, 0.4))'
                        }}
                      />
                    )}
                  </motion.g>

                  {/* Hover / locked photo card - click opens the photo full size
                      (or the Flickr album for launch celebrations) */}
                  {activePin === stop.id && photo && (
                    <motion.g
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (stop.flickrUrl) {
                          window.open(stop.flickrUrl, '_blank', 'noopener');
                        } else {
                          setLightboxStop(stop);
                        }
                      }}
                      style={{ cursor: stop.flickrUrl ? 'pointer' : 'zoom-in' }}
                    >
                      <rect
                        x={cardX}
                        y={cardY}
                        width={CARD_WIDTH}
                        height={cardHeight}
                        rx={8}
                        fill="white"
                        stroke="#DC143C"
                        strokeWidth={2}
                        style={{
                          filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2))'
                        }}
                      />
                      <image
                        href={photo}
                        x={cardX + CARD_PAD}
                        y={cardY + CARD_PAD}
                        width={IMAGE_WIDTH}
                        height={IMAGE_HEIGHT}
                        preserveAspectRatio="xMidYMid slice"
                        style={{ clipPath: 'inset(0 round 4px)' }}
                      />
                      <text
                        x={cardX + CARD_WIDTH / 2}
                        y={cardY + CARD_PAD + IMAGE_HEIGHT + 21}
                        textAnchor="middle"
                        style={{
                          fontFamily: 'inherit',
                          fontSize: '13px',
                          fontWeight: 700,
                          fill: '#DC143C',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {stop.city}, {stop.state}
                      </text>
                      {isLaunch && (
                        <text
                          x={cardX + CARD_WIDTH / 2}
                          y={cardY + CARD_PAD + IMAGE_HEIGHT + 41}
                          textAnchor="middle"
                          style={{
                            fontFamily: 'inherit',
                            fontSize: '10px',
                            fontWeight: 600,
                            fill: '#666',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          View the Photo Album {'→'}
                        </text>
                      )}
                    </motion.g>
                  )}
                </g>
              </Marker>
            );
          })}
        </ComposableMap>
      </div>

      {/* Photo lightbox - portaled to <body> so position:fixed covers the whole
          viewport (transformed ancestors would otherwise trap and clip it) */}
      {createPortal(
      <AnimatePresence>
        {lightboxStop && (
          <motion.div
            className="map-lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightboxStop(null)}
          >
            <motion.figure
              className="map-lightbox-frame"
              initial={{ scale: 0.92 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.92 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="map-lightbox-close"
                aria-label="Close photo"
                onClick={() => setLightboxStop(null)}
              >
                &times;
              </button>
              <img
                src={stopPhotos[lightboxStop.id]}
                alt={`Tour of a Lifetime stop in ${lightboxStop.city}, ${lightboxStop.state}`}
              />
              <figcaption>
                {lightboxStop.city}, {lightboxStop.state}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </div>
  );
}

export default USAMap;
